// src/components/child/line_bars.jsx
import React, { useRef, useEffect, useMemo, useState } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../BoardPack/components/shared/heading";

// ✅ Reusable Component with Ramadan Detection + DARK MODE SUPPORT + SHADOWS
const LineBar = ({
  title,
  subtitle,
  in_chart_content_line_1,
  datasets,
  months,
  yearRanges,
  yAxisTitle = "Average Daily Deployed",
  yMin = null,
  yMax = null,
  showBars = false,
  ramadanRevenueData = [],
  barDatasets = [],
  showSecondTable = false,
  secondTableData = null,
  showRamadanRow = false,
  ramadanborderColor = "#FFA500",
  topRightContent = null,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [themeKey, setThemeKey] = useState(0);

  // ✅ Store value label rects for Ramadan avoidance
  const valueLabelRects = useRef([]);
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  // Chart.js reserves left/right margin width for the y-axis tick labels —
  // kept in sync with the chart's actual measured insets (via layoutSyncPlugin
  // below) so the table's edge columns stay pixel-aligned with the chart above
  // them, same approach as NewChartKPI.
  const [plotLeftInset, setPlotLeftInset] = useState(100);
  const [plotRightInset, setPlotRightInset] = useState(5);
  // One uniform font size for every data cell — sized to fit the single
  // widest value in the table, so every cell (biggest and smallest value
  // alike) renders at the same size.
  const [cellFontSize, setCellFontSize] = useState(10);

  // ✅ Detect theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setThemeKey((prev) => prev + 1);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // ✅ Ramadan Detection Function (returns array with month indices)
  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !months || !yearRanges) return [];

    const ramadanMonthIndices = [];
    let currentMonthIndex = 0;

    yearRanges.forEach((yearRange) => {
      const year = parseInt(yearRange.year);
      for (let i = 0; i < yearRange.span; i++) {
        const monthName = months[currentMonthIndex];
        const monthMap = {
          Jan: 0, Feb: 1, Mar: 2, Apr: 3,
          May: 4, Jun: 5, Jul: 6, Aug: 7,
          Sep: 8, Oct: 9, Nov: 10, Dec: 11,
        };
        const monthNum =
          monthMap[monthName] !== undefined
            ? monthMap[monthName]
            : currentMonthIndex % 12;
        const hijriYear = year - 579;
        const ramadanFirstDay = moment(`${hijriYear}-9-1`, 'iYYYY-iM-iD');
        const ramadanStartGregMonth = ramadanFirstDay.month();
        const ramadanStartGregYear = ramadanFirstDay.year();

        if (
          ramadanStartGregYear === year &&
          ramadanStartGregMonth === monthNum
        ) {
          ramadanMonthIndices.push(currentMonthIndex);
        }
        currentMonthIndex++;
      }
    });

    return ramadanMonthIndices;
  }, [months, yearRanges, showRamadanRow]);

  // ✅ Auto-calculate Y-axis range from data
  const calculateYAxisRange = () => {
    const allValues = [];
    datasets.forEach((ds) => {
      ds.data.forEach((val) => {
        if (typeof val === "number" && !isNaN(val)) allValues.push(val);
      });
    });
    if (showBars && barDatasets.length > 0) {
      barDatasets.forEach((ds) => {
        ds.data.forEach((val) => {
          if (Array.isArray(val)) {
            val.forEach((v) => {
              if (typeof v === "number" && !isNaN(v)) allValues.push(v);
            });
          } else if (typeof val === "number" && !isNaN(val)) {
            allValues.push(val);
          }
        });
      });
    }
    if (allValues.length === 0) return { min: 0, max: 100 };
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    let padding;
    if (dataMax <= 2) {
      padding = (dataMax - dataMin) * 1;
    } else {
      padding = (dataMax - dataMin) * 0.25;
    }
    return {
      min: Math.max(0, Math.round(dataMin - padding)),
      max: Math.round(dataMax + padding),
    };
  };

  const yAxisRange = {
    min: yMin !== null ? yMin : calculateYAxisRange().min,
    max: yMax !== null ? yMax : calculateYAxisRange().max,
  };

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    const existingChart = Chart.Chart.getChart(chartRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    const textColor = "#e5e7eb";
    const labelTextColor = "#ffffff";

    // ✅ This will be populated by dataLabelsPlugin and used by ramadanPlugin
    const finalLabelRects = [];

    // ✅ LINE SHADOW PLUGIN
    const lineShadowPlugin = {
      id: "lineShadow",
      beforeDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type === "bar") return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.shadowColor = dataset.borderColor || "rgba(255,255,255,0.8)";
        ctx.shadowBlur = 18;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      },
      afterDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type === "bar") return;
        chart.ctx.restore();
      },
    };

    // ✅ BAR SHADOW PLUGIN
    const barShadowPlugin = {
      id: "barShadow",
      beforeDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type !== "bar") return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 4;
      },
      afterDatasetDraw(chart, args) {
        const dataset = chart.data.datasets[args.index];
        if (dataset.type !== "bar") return;
        chart.ctx.restore();
      },
    };

    // ✅ Data Labels Plugin — NOW DRAWS PLAIN COLORED TEXT (no box/background)
    const dataLabelsPlugin = {
      id: "customDataLabels",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        // ✅ Smaller font + tighter padding when there are many datasets/points so labels don't clash
        const totalLineDs = chart.data.datasets.filter((d) => d.type !== "bar").length;
        const fontSize = totalLineDs > 3 ? 10 : 11;
        const padding = 3;

        // Clear previous rects
        finalLabelRects.length = 0;

        const ramadanMonthIndices = new Set(getRamadanMonths);
        const labels = [];

        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          if (meta.hidden || dataset.type === "bar") return;

          meta.data.forEach((element, index) => {
            const value = dataset.data[index];

            // Ramadan label wali exact value hide karo
            if (ramadanMonthIndices.has(index)) {
              const barVal =
                ramadanRevenueData?.[index] ??
                chart.data.datasets.find(d => d.type === "bar")?.data?.[index];

              // Woh line dataset dhundo jis ki value bar se match karti hai
              const lineDatasets = chart.data.datasets.filter(d => d.type !== "bar");
              const matchingDataset = lineDatasets.find(d => d.data[index] === barVal);

              // Sirf usi dataset ka label hide karo
              if (matchingDataset && dataset.label === matchingDataset.label) return;
            }

            if (value == null || value === 0) return;

            const x = element?.x;
            const y = element?.y;

            if (x == null || y == null || isNaN(x) || isNaN(y)) return;

            const color = dataset.borderColor;
            const lineDatasets = chart.data.datasets.filter(
              (ds) => ds.type !== "bar"
            );
            const lineIndex = lineDatasets.findIndex(
              (ds) => ds.label === dataset.label
            );
            // ✅ Give every dataset its own vertical band around its line so labels from
            // different datasets don't collide by default (instead of just 2 fixed spots)
            const bandCount = Math.max(lineDatasets.length, 1);
            const mid = (bandCount - 1) / 2;
            const bandStep = 22;
            const baseYOffset = -18 + (lineIndex - mid) * bandStep * -1;

            const text =
              typeof value === "number" && !isNaN(value)
                ? value.toLocaleString()
                : value?.toString() || "0";

            ctx.font = `bold ${fontSize}px Arial`;
            const textWidth = ctx.measureText(text).width;
            const boxWidth = textWidth + padding * 4;
            const boxHeight = 20;

            labels.push({
              x,
              y,
              text,
              color,
              width: boxWidth,
              height: boxHeight,
              baseY: y + baseYOffset,
              datasetIndex: i,
              pointIndex: index,
            });
          });
        });

        labels.sort((a, b) => a.x - b.x);
        labels.forEach((l) => { l.finalY = l.baseY; });

        const V_GAP = 6;  // min vertical gap between labels
        const H_GAP = 6;  // min horizontal gap

        // Returns true if two label rects overlap (with gaps)
        const rectsOverlap = (a, b) => {
          const aL = a.x - a.width / 2 - H_GAP;
          const aR = a.x + a.width / 2 + H_GAP;
          const aT = a.finalY - a.height / 2 - V_GAP;
          const aB = a.finalY + a.height / 2 + V_GAP;
          const bL = b.x - b.width / 2;
          const bR = b.x + b.width / 2;
          const bT = b.finalY - b.height / 2;
          const bB = b.finalY + b.height / 2;
          return !(aR < bL || aL > bR || aB < bT || aT > bB);
        };

        // Multi-pass resolution: each pass tries to push overlapping labels apart
        // Labels above their data point push UP, labels below push DOWN
        const MAX_PASSES = 50;
        for (let pass = 0; pass < MAX_PASSES; pass++) {
          let anyMoved = false;
          for (let i = 0; i < labels.length; i++) {
            for (let j = i + 1; j < labels.length; j++) {
              const a = labels[i];
              const b = labels[j];
              if (!rectsOverlap(a, b)) continue;

              anyMoved = true;
              // Determine push direction for each label based on whether it is
              // above or below its anchor point (b.y = line pixel Y)
              const aAbove = a.finalY < a.y; // label is above data point
              const bAbove = b.finalY < b.y;

              const overlapAmt =
                (a.finalY + a.height / 2 + V_GAP) -
                (b.finalY - b.height / 2);

              // Chart bounds se bahar na jayein
              const chartT = chart.scales.y.top + 32;
              const chartB = chart.scales.y.bottom - 4;

              if (a.x === b.x || Math.abs(a.x - b.x) < (a.width + b.width) / 2) {
                // They share horizontal space — push vertically
                if (aAbove && bAbove) {
                  // Both above: push the higher one further up
                  if (a.finalY <= b.finalY) {
                    a.finalY = Math.max(chartT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(chartB, b.finalY + overlapAmt / 2);
                  } else {
                    b.finalY -= overlapAmt / 2;
                    a.finalY += overlapAmt / 2;
                  }
                } else if (!aAbove && !bAbove) {
                  // Both below: push lower one further down
                  if (a.finalY >= b.finalY) {
                    b.finalY -= overlapAmt / 2;
                    a.finalY += overlapAmt / 2;
                  } else {
                    a.finalY = Math.max(chartT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(chartB, b.finalY + overlapAmt / 2);
                  }
                } else {
                  // One above, one below: push each to their respective sides
                  if (aAbove) {
                    a.finalY = Math.max(chartT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(chartB, b.finalY + overlapAmt / 2);
                  } else {
                    a.finalY = Math.max(chartT, a.finalY - overlapAmt / 2);
                    b.finalY = Math.min(chartB, b.finalY + overlapAmt / 2);
                  }
                }
              }
            }
          }
          if (!anyMoved) break;
        }

        labels.forEach((lbl) => {
          if (lbl.finalY == null || isNaN(lbl.finalY)) return;

          const boxX = lbl.x - lbl.width / 2;
          const boxY = lbl.finalY - lbl.height / 2;

          // ✅ Still track rects so Ramadan boxes avoid overlapping the text
          finalLabelRects.push({
            left: boxX - 6,
            right: boxX + lbl.width + 6,
            top: boxY - 2,
            bottom: boxY + lbl.height + 6,
          });

          // ✅ NO BOX / NO BACKGROUND — plain text in the dataset's own color
          ctx.save();
          ctx.fillStyle = lbl.color;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          ctx.fillText(lbl.text, lbl.x, lbl.finalY);
          ctx.restore();
        });
      },
    };


    // Keeps the table's first/last column widths in sync with the chart's
    // actual measured left/right insets (varies with y-axis tick label width)
    // instead of a fixed guess, so month columns stay pixel-aligned with the
    // chart above them — same approach as NewChartKPI.
    const layoutSyncPlugin = {
      id: "layoutSync",
      afterLayout(chart) {
        const left = Math.round(chart.chartArea.left);
        const right = Math.round(chart.width - chart.chartArea.right);
        setPlotLeftInset((prev) => (prev === left ? prev : left));
        setPlotRightInset((prev) => (prev === right ? prev : right));
      },
    };

    // ✅ RAMADAN PLUGIN - Sirf Bar Dataset ki value show karega
    const ramadanPlugin = showRamadanRow
      ? {
        id: "ramadanPlugin",
        beforeDatasetsDraw(chart) {
          const ctx = chart.ctx;
          const xAxis = chart.scales.x;
          const yAxis = chart.scales.y;

          const ramadanMonthIndices = getRamadanMonths;
          if (ramadanMonthIndices.length === 0) return;

          const BOX_H = 20;
          const ARROW_W = 10;
          const H_PAD = 4;
          const V_PAD = 4;
          const BR = 3;

          const chartTop = yAxis.top;
          const chartBottom = yAxis.bottom;
          const chartLeft = xAxis.left;
          const chartRight = xAxis.right;

          // BOX_W is computed per-item based on actual text — see inside forEach

          const maxLabelY = chartTop + ((chartBottom - chartTop) * 0.60);

          // Chart boundary clamp — lets xOffsets actually take effect
          const clampX = (x, _anchorX, boxW) => {
            return Math.max(chartLeft + 2, Math.min(chartRight - boxW - 2, x));
          };

          const clampYAbove = (y) => {
            return Math.max(chartTop + 2, Math.min(maxLabelY, y));
          };

          const mkRect = (l, t, w, h) => ({
            left: l, right: l + w, top: t, bottom: t + h,
          });

          const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
            a.left - hPad < b.right + hPad &&
            a.right + hPad > b.left - hPad &&
            a.top - vPad < b.bottom + vPad &&
            a.bottom + vPad > b.top - vPad;

          const obstacles = [];

          finalLabelRects.forEach((r) =>
            obstacles.push({
              left: r.left - 3,
              right: r.right + 3,
              top: r.top - 2,
              bottom: r.bottom + 2,
            })
          );

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type !== "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((bar) => {
              if (!bar) return;
              try {
                const props = bar.getProps(["x", "y", "base", "width"], true);
                const barLeft = props.x - props.width / 2;
                const barRight = props.x + props.width / 2;
                const barTop = Math.min(props.y, props.base);
                const barBottom = Math.max(props.y, props.base);
                if (!isNaN(barLeft) && !isNaN(barTop)) {
                  obstacles.push({
                    left: barLeft - 2,
                    right: barRight + 2,
                    top: barTop - 2,
                    bottom: barBottom + 2,
                  });
                }
              } catch (_) { }
            });
          });

          chart.data.datasets.forEach((dataset, dsIdx) => {
            if (dataset.type === "bar") return;
            const meta = chart.getDatasetMeta(dsIdx);
            if (meta.hidden) return;
            meta.data.forEach((pt) => {
              if (!pt) return;
              const px = pt.x;
              const py = pt.y;
              if (px == null || py == null || isNaN(px) || isNaN(py)) return;
              obstacles.push({
                left: px - 5,
                right: px + 5,
                top: py - 5,
                bottom: py + 5,
              });
            });
          });

          const getBarTopYForIndex = (idx) => {
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const meta = chart.getDatasetMeta(i);
              const dataset = chart.data.datasets[i];
              if (dataset.type === "bar" && meta?.data?.[idx]) {
                const bar = meta.data[idx];
                try {
                  const props = bar.getProps(["y", "base"], true);
                  return Math.min(props.y, props.base ?? props.y);
                } catch (_) {
                  return Math.min(bar.y, bar.base ?? bar.y);
                }
              }
            }
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const meta = chart.getDatasetMeta(i);
              if (meta.hidden) continue;
              if (meta?.data?.[idx] && meta.data[idx].y !== undefined && !isNaN(meta.data[idx].y)) {
                return meta.data[idx].y;
              }
            }
            return yAxis.getPixelForValue(0);
          };

          // 🔥 FIXED: Sirf BAR dataset ki value lega (Line datasets ignore)
          const getRevenueForIndex = (idx) => {
            // 1. Check ramadanRevenueData (explicitly passed)
            if (ramadanRevenueData && ramadanRevenueData[idx] !== undefined) {
              const val = ramadanRevenueData[idx];
              if (typeof val === "number" && !isNaN(val)) return val;
              if (Array.isArray(val) && val.length > 1) {
                const numVal = parseFloat(val[1]);
                if (!isNaN(numVal)) return numVal;
              }
              return 0;
            }

            // 2. Sirf BAR datasets se value lo
            for (let i = 0; i < chart.data.datasets.length; i++) {
              const dataset = chart.data.datasets[i];
              if (dataset.type === "bar" && dataset.data && dataset.data[idx] !== undefined) {
                const val = dataset.data[idx];
                if (typeof val === "number" && !isNaN(val)) return val;
                if (Array.isArray(val) && val.length > 1) {
                  const numVal = parseFloat(val[1]);
                  if (!isNaN(numVal)) return numVal;
                }
                return 0;
              }
            }

            // 3. LINE datasets ko IGNORE karo - sirf bar ki value show hogi
            // Reusable chart jaisa behavior
            return 0;
          };

          const getMonthName = (idx) => months[idx] || "";
          const getYearForIndex = (idx) => {
            let counter = 0;
            for (const yr of yearRanges) {
              const end = counter + yr.span;
              if (idx >= counter && idx < end) return yr.year;
              counter += yr.span;
            }
            return "";
          };

          const itemsMap = new Map();

          ramadanMonthIndices.forEach((idx) => {
            const val = getRevenueForIndex(idx);
            const year = getYearForIndex(idx);
            const monthName = getMonthName(idx);

            // Sirf tabhi label show karo jab value > 0 ho
            // if (val === 0 || val === null) return;

            const key = `${year}-${monthName}`;

            if (!itemsMap.has(key)) {
              const formattedVal = typeof val === "number" && !isNaN(val) ? val.toLocaleString() : "0";
              const dataX = xAxis.getPixelForValue(idx);
              const dataY = getBarTopYForIndex(idx);
              itemsMap.set(key, {
                label: `RAMADHAN ${year} ${monthName} : ${formattedVal}`,
                dataX, dataY, monthIndex: idx, year, value: val,
              });
            }
          });

          const items = Array.from(itemsMap.values()).sort((a, b) => a.year - b.year);

          if (items.length === 0) return;

          // Place box just ABOVE the data point — relative to actual bar/line height
          const ROW_H = BOX_H + 6;
          const GAP_ABOVE = 10; // gap between box bottom and arrow tip
          const placedBoxes = [];

          // Sort left-to-right so overlap resolution is natural
          const sortedItems = [...items].sort((a, b) => a.dataX - b.dataX);

          sortedItems.forEach(({ label, dataX, dataY }) => {
            const arrowTipY = dataY;

            // Compute BOX_W from full single-line label text + padding
            ctx.font = "bold 10px Arial";
            const BOX_W = ctx.measureText(label).width + 20;

            // Base position: just above the data point
            const maxBoxY = chartBottom - BOX_H - 20;
            const baseY = Math.min(arrowTipY - BOX_H - GAP_ABOVE, maxBoxY);

            // Try X offsets × Y rows — fine-grained steps so label stays as close to bar as possible
            // Right-half bars try left first, left-half bars try right first
            const chartMidX = (chartLeft + chartRight) / 2;
            const step = BOX_W * 0.3;
            const xOffsets = dataX > chartMidX
              ? [0, -step, step, -2*step, 2*step, -3*step, 3*step, -4*step, 4*step, -5*step, 5*step]
              : [0, step, -step, 2*step, -2*step, 3*step, -3*step, 4*step, -4*step, 5*step, -5*step];
            let chosenX = null;
            let chosenY = null;
            let prevTryY = null;

            outer: for (const xOff of xOffsets) {
              const tryX = clampX(dataX - BOX_W / 2 + xOff, dataX, BOX_W);
              prevTryY = null;
              for (let row = 0; row < 14; row++) {
                const tryY = Math.max(chartTop + 4, baseY - row * ROW_H);
                // Stop trying more rows once we've hit the ceiling
                if (prevTryY !== null && tryY === prevTryY) break;
                prevTryY = tryY;
                const testRect = mkRect(tryX, tryY, BOX_W, BOX_H);
                // placedBoxes (other Ramadan labels) ke liye strict check, value labels aur bars ke liye lenient
                const blocked =
                  placedBoxes.some((obs) => overlaps(testRect, obs, 4, 4)) ||
                  finalLabelRects.some((obs) => overlaps(testRect, obs, 2, 1)) ||
                  obstacles.some((obs) => overlaps(testRect, obs, 2, 2));
                if (!blocked) {
                  chosenX = tryX;
                  chosenY = tryY;
                  break outer;
                }
              }
            }

            // Fallback: place directly above if all spots blocked
            if (chosenX === null) {
              chosenX = clampX(dataX - BOX_W / 2, dataX, BOX_W);
              chosenY = Math.max(chartTop + 4, baseY);
            }

            if (chosenX !== null && chosenY !== null) {
              const RAMADAN_COLOR = ramadanborderColor || "#ffa500";

              placedBoxes.push(mkRect(chosenX, chosenY, BOX_W, BOX_H));

              const bTop = chosenY;
              const bBot = chosenY + BOX_H;
              const baseCX = Math.min(
                Math.max(dataX, chosenX + ARROW_W / 2 + BR),
                chosenX + BOX_W - ARROW_W / 2 - BR
              );
              const baseBL = baseCX - ARROW_W / 2;
              const baseBR = baseCX + ARROW_W / 2;

              ctx.save();

              ctx.beginPath();
              ctx.moveTo(chosenX + BR, bTop);
              ctx.lineTo(chosenX + BOX_W - BR, bTop);
              ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
              ctx.lineTo(chosenX + BOX_W, bBot);
              ctx.lineTo(baseBR, bBot);
              ctx.lineTo(dataX, arrowTipY);
              ctx.lineTo(baseBL, bBot);
              ctx.lineTo(chosenX, bBot);
              ctx.lineTo(chosenX, bTop + BR);
              ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
              ctx.closePath();

              ctx.shadowColor = RAMADAN_COLOR;
              ctx.shadowBlur = 10;
              ctx.shadowOffsetX = 0;
              ctx.shadowOffsetY = 0;
              ctx.strokeStyle = RAMADAN_COLOR;
              ctx.lineWidth = 1.5;
              ctx.stroke();
              ctx.shadowColor = "transparent";
              ctx.shadowBlur = 0;

              ctx.fillStyle = "#ffffff";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.font = "bold 10px Arial";
              ctx.fillText(label, chosenX + BOX_W / 2, chosenY + BOX_H / 2);

              ctx.restore();
            }
          });
        },
      }
      : { id: "ramadanPlugin_disabled", afterDraw() { } };

    // ✅ Build chart datasets
    const chartDatasets = datasets.map((ds) => ({
      label: ds.label,
      data: ds.data.map((val) => (val == null ? 0 : val)),
      type: ds.type || "line",
      borderColor: ds.color,
      backgroundColor: ds.color,
      pointBackgroundColor: ds.color,
      pointBorderColor: ds.color,
      pointStyle: "rect",
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1.5,
      tension: 0,
      borderDash: ds.borderDash || [],
      order: ds.order || 0,
    }));

    if (showBars && barDatasets.length > 0) {
      barDatasets.forEach((barDs) => {
        if (!barDs.data || barDs.data.length === 0) return;
        const hasValidData = barDs.data.some(
          (v) => v !== null && v !== undefined
        );
        if (!hasValidData) return;

        chartDatasets.push({
          label: barDs.label,
          data: barDs.data,
          type: "bar",
          backgroundColor: barDs.color || "rgba(150, 150, 150, 0.6)",
          borderColor: "black",//barDs.color || "rgba(150, 150, 150, 1)",
          borderWidth: 1,
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          order: barDs.order || 0,
          legendHidden: true,
        });
      });
    }

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "line",
      data: { labels: months, datasets: chartDatasets },
      options: {
        devicePixelRatio: (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1) * 2,
        responsive: false,
        maintainAspectRatio: false,
        clip: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: textColor,
              font: { size: 10 },
              usePointStyle: true,
              pointStyle: "line",
              padding: 15,
              boxWidth: 400,
              boxHeight: 20,
              filter: function (legendItem, chartData) {
                const dataset = chartData.datasets[legendItem.datasetIndex];
                return dataset.type !== "bar";
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "#475569",
            borderWidth: 1,
            padding: 8,
            displayColors: true,
            callbacks: {
              label: function (context) {
                const val = context.parsed.y;
                return (
                  context.dataset.label +
                  ": " +
                  (typeof val === "number" ? val.toLocaleString() : val)
                );
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { display: false },
            border: { display: false },
            offset: true,
          },
          y: {
            min: yAxisRange.min,
            max: yAxisRange.max,
            clip: false,
            ticks: {
              color: textColor,
              font: { size: 9 },
              maxTicksLimit: 20,
              callback: function (value) {
                return typeof value === "number"
                  ? value.toLocaleString()
                  : value;
              },
            },
            grid: { display: false },
            border: { display: false },
            // title: {
            //   display: true,
            //   text: yAxisTitle,
            //   color: textColor,
            //   font: { size: 10 },
            // },
          },
        },
        layout: {
          padding: { left: 10, right: 5, top: 10, bottom: 45 },
        },
      },
      plugins: [lineShadowPlugin, barShadowPlugin, dataLabelsPlugin, ramadanPlugin, layoutSyncPlugin],
    });

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartInstanceRef.current) return;
      const w = containerRef.current.offsetWidth;
      const h = containerRef.current.offsetHeight;
      chartInstanceRef.current.resize(w, h);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [
    datasets,
    barDatasets,
    months,
    yAxisRange.min,
    yAxisRange.max,
    yAxisTitle,
    showBars,
    showRamadanRow,
    getRamadanMonths,
    ramadanborderColor,
    yearRanges,
    themeKey,
  ]);

  const allDatasets = [...datasets, ...(showBars ? barDatasets : [])];
  const tableDatasets = allDatasets.filter((ds) => !ds.hideInTable);

  // Data-cell font size: measure the widest formatted value against the
  // width actually available per month column (card width minus the chart's
  // left/right insets, split across months) and shrink the font until it
  // fits — same approach as NewChartKPI, so both tables scale identically.
  useEffect(() => {
    if (!cardRef.current || !months.length) return;

    const measureCanvas = document.createElement("canvas");
    const mctx = measureCanvas.getContext("2d");
    const BASE_SIZE = 10;
    const MIN_SIZE = 5;
    const CELL_PAD = 4; // px-0.5 padding + border, both sides

    const recompute = () => {
      const totalWidth = cardRef.current?.offsetWidth ?? 0;
      const numCols = months.length;
      if (!totalWidth || !numCols) return;

      const perCol = Math.max(0, (totalWidth - plotLeftInset - plotRightInset) / numCols - CELL_PAD);

      mctx.font = `${BASE_SIZE}px Arial`;
      let widest = 0;
      tableDatasets.forEach((ds) => {
        ds.data.forEach((val) => {
          const text = Array.isArray(val) ? "-" : val == null ? "0" : typeof val === "number" ? val.toLocaleString() : String(val);
          widest = Math.max(widest, mctx.measureText(text).width);
        });
      });

      const size = widest > perCol && widest > 0
        ? Math.max(MIN_SIZE, Math.floor(BASE_SIZE * (perCol / widest)))
        : BASE_SIZE;
      setCellFontSize((prev) => (prev === size ? prev : size));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [tableDatasets, months, plotLeftInset, plotRightInset]);

  const BoxIndicator = ({ color }) => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: color, borderRadius: 1 }} />
    </span>
  );
  const LineIndicator = ({ color }) => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 18, height: 2, backgroundColor: color }} />
    </span>
  );

  const safeValue = (val) => {
    if (Array.isArray(val)) return "-";
    if (val == null) return "0";
    return typeof val === "number" ? val.toLocaleString() : val;
  };

  return (
    <div className="w-full">
      <div
        className="w-full mx-auto px-1 py-1 bg-card"
      >
        {/* Header Section */}
        <div className="text-center">
          {title && (
            <HeadingComponent variant={"main"}>{title}</HeadingComponent>
          )}
          {subtitle && (
            <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
          )}
        </div>

        {/* Chart + Table */}
        <div
          ref={cardRef}
          className="w-full p-0.5"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          {in_chart_content_line_1 && (
            <div className="text-center mt-5">
              <h3
                className="text-xs font-semibold tracking-wider"
                style={{ color: "#ffffff" }}
              >
                {in_chart_content_line_1}
              </h3>
            </div>
          )}

          {/* ✅ zoom removed — chart now renders at native 100% size */}
          <div
            className="h-[550px] relative"
            ref={containerRef}
            style={{ marginBottom: "0px" }}
          >
            <canvas ref={chartRef}></canvas>
            {topRightContent && (
              <div className="absolute top-2 right-2 z-10">{topRightContent}</div>
            )}
          </div>

          <div className="w-full">
            <div className="overflow-x-auto">
              <table className="w-full !border-collapse text-xs table-fixed">
                <thead>
                  <tr>
                    <th className="py-0.5 px-1 text-center font-normal" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></th>
                    {months.map((month, idx) => (
                      <th
                        key={idx}
                        className="!border !border-border px-0.5 py-0.5 text-center font-normal text-[10px] transition-colors hover:bg-white/15"
                        style={{ color: "#ffffff", borderColor: "#4b5563" }}
                      >
                        {month}
                      </th>
                    ))}
                    {plotRightInset > 0 && <th style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></th>}
                  </tr>
                  <tr>
                    <td className="py-0.5 px-1" style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px` }}></td>
                    {yearRanges.map((range, idx) => (
                      <td key={idx} colSpan={range.span} className="!border !border-border px-0.5 py-0.5 text-center text-[10px] font-bold" style={{ color: "#ffffff", borderColor: "#4b5563" }}>
                        {range.year}
                      </td>
                    ))}
                    {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                  </tr>
                </thead>
                <tbody>
                  {tableDatasets.map((dataset, dsIdx) => {
                    const Indicator = dataset.type === "bar" ? BoxIndicator : LineIndicator;
                    return (
                      <tr key={dsIdx}>
                        <td
                          className="!border !border-border px-1 py-1.5 font-normal text-[8px] text-left whitespace-nowrap overflow-hidden text-ellipsis"
                          style={{ width: `${plotLeftInset}px`, minWidth: `${plotLeftInset}px`, color: "#ffffff", borderColor: "#4b5563" }}
                          title={dataset.label}
                        >
                          <Indicator color={dataset.color} />
                          {dataset.label}
                        </td>
                        {dataset.data.map((val, idx) => (
                          <td
                            key={idx}
                            className="!border !border-border px-0.5 py-1.5 text-center whitespace-nowrap overflow-hidden transition-colors hover:bg-white/15"
                            style={{ color: "#ffffff", borderColor: "#4b5563", fontSize: `${cellFontSize}px` }}
                            title={String(safeValue(val))}
                          >
                            {safeValue(val)}
                          </td>
                        ))}
                        {plotRightInset > 0 && <td style={{ width: `${plotRightInset}px`, minWidth: `${plotRightInset}px` }}></td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-center py-0.5 mt-4 text-[10px] font-bold" style={{ color: "#ffffff" }}>
                Months
              </div>
            </div>
          </div>
        </div>

        {/* Second Table */}
        {showSecondTable && secondTableData && (
          <div className="w-full mt-4 overflow-visible" style={{ backgroundColor: "#ffffff" }}>
            <table
              className="w-full !border-collapse table-auto"
              style={{ tableLayout: "auto" }}
            >
              <thead>
                <tr>
                  <th
                    className="px-3 py-2 text-left font-bold"
                    style={{
                      backgroundColor: "#1a3a5c",
                      color: "#ffffff",
                      border: "1px solid #4a6fa5",
                      width: "20%",
                      fontSize: "clamp(8px, 0.9vw, 11px)",
                    }}
                  >
                    {secondTableData.title}
                  </th>
                  {[
                    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
                  ].map((m, idx) => (
                    <th
                      key={idx}
                      className="px-1 py-2 text-center font-bold"
                      style={{
                        backgroundColor: "#1a3a5c",
                        color: "#ffffff",
                        border: "1px solid #4a6fa5",
                        fontSize: "clamp(8px, 0.9vw, 11px)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {secondTableData.rows &&
                  secondTableData.rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td
                        className="px-2 py-1.5 text-center font-bold"
                        style={{
                          backgroundColor: "#1a3a5c",
                          color: "#ffffff",
                          border: "1px solid #4a6fa5",
                          fontSize: "clamp(8px, 0.9vw, 11px)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.label}
                      </td>
                      {Array.from({ length: 12 }).map((_, idx) => {
                        const val = row.data[idx];
                        const displayVal =
                          val == null
                            ? "0%"
                            : typeof val === "number"
                              ? val.toLocaleString() + "%"
                              : val;
                        return (
                          <td
                            key={idx}
                            className="px-1 py-1.5 text-center"
                            style={{
                              backgroundColor: "#ffffff",
                              color: "#1a1a1a",
                              border: "1px solid #4a6fa5",
                              fontWeight: "400",
                              fontSize: "clamp(8px, 0.9vw, 11px)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {displayVal}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineBar;